import React, {useEffect, useState} from "react";
import {Alert, Autocomplete, Box, Button, CircularProgress, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {getCourierUsers} from "./courier-api";

export default function CourierUserPicker({value, onChange, tenantId, disabled}) {
    const {t, i18n} = useTranslation();
    const [search, setSearch] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [page, setPage] = useState(0);
    const [version, setVersion] = useState(0);
    const [result, setResult] = useState({users: [], loading: true, error: "", totalPages: 0});
    const language = i18n.language;
    const userLabel = user => `${[user.name, user.surname].filter(Boolean).join(" ")} (${user.email})`;

    useEffect(() => {
        const controller = new AbortController();
        setResult(previous => ({...previous, loading: true, error: ""}));
        const timeout = setTimeout(() => getCourierUsers(search, page, language, controller.signal).then(data => {
            if (controller.signal.aborted) return;
            const users = data.content.filter(user => user.tenantId === tenantId && user.active && user.role === "COURIER");
            setResult(previous => ({users: page === 0 ? users : [...previous.users, ...users],
                loading: false, error: "", totalPages: data.totalPages}));
        }).catch(error => {
            if (!controller.signal.aborted) setResult(previous => ({...previous, loading: false,
                error: error.response?.data?.message || "TenantUsersLoadFailed"}));
        }), search ? 300 : 0);
        return () => { clearTimeout(timeout); controller.abort(); };
    }, [search, page, tenantId, language, version]);

    return <Box sx={{mb: 3}}>
        <Autocomplete options={result.users} value={value} inputValue={inputValue} disabled={disabled} loading={result.loading}
                      filterOptions={users => users} isOptionEqualToValue={(left, right) => left.id === right.id}
                      getOptionLabel={userLabel}
                      onChange={(_, user) => { onChange(user); setInputValue(user ? userLabel(user) : ""); }}
                      noOptionsText={t("NoActiveCourierUsers")}
                      loadingText={t("Loading")}
                      onInputChange={(_, text, reason) => {
                          if (reason !== "input" && reason !== "clear") return;
                          // Keep typed text when clearing a previous selection; MUI's automatic reset would erase it.
                          setInputValue(text);
                          onChange(null);
                          setSearch(text); setPage(0);
                          setResult({users: [], loading: true, error: "", totalPages: 0});
                      }}
                      renderInput={params => <TextField {...params} label={t("CourierUser")} required
                          helperText={t("CourierUserHint")} InputProps={{...params.InputProps,
                              endAdornment: <>{result.loading && <CircularProgress size={18}/>}{params.InputProps.endAdornment}</>}}/>}/>
        {result.error && <Alert severity="error" sx={{mt: 1}} action={<Button disabled={disabled} color="inherit"
            onClick={() => setVersion(value => value + 1)}>{t("Retry")}</Button>}>{t(result.error)}</Alert>}
        {!result.loading && !result.error && page + 1 < result.totalPages && <Button disabled={disabled}
            onClick={() => setPage(value => value + 1)}>{t("LoadMoreCourierUsers")}</Button>}
    </Box>;
}
